import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import {
  createPublicClient,
  createWalletClient,
  custom,
  fallback,
  getAddress,
  http,
  type PublicClient,
  type WalletClient,
} from "viem";
import { robinhoodTestnet } from "./chain";
import { ACTIVE_CHAIN } from "../config/chains";
import type { Hex } from "../services/launcher/types";

/** Minimal EIP-1193 provider shape (injected wallets: MetaMask, Rabby, …). */
interface Eip1193Provider {
  request(args: { method: string; params?: unknown[] | object }): Promise<unknown>;
  on?(event: string, handler: (...args: unknown[]) => void): void;
  removeListener?(event: string, handler: (...args: unknown[]) => void): void;
}
declare global {
  interface Window {
    ethereum?: Eip1193Provider;
  }
}

export interface WalletState {
  address: Hex | null;
  isConnected: boolean;
  chainId: number | null;
  balance: bigint | null;
  hasInjected: boolean;
  connecting: boolean;
  /** Always available: reads for the active chain over HTTP. */
  publicClient: PublicClient;
  /** Available only while connected. Writes are signed here. */
  walletClient: WalletClient | null;
  connect: () => Promise<void>;
  disconnect: () => void;
  switchToTestnet: () => Promise<void>;
  addTestnet: () => Promise<void>;
  watchAsset: (token: { address: Hex; symbol: string; decimals: number }) => Promise<boolean>;
}

const WalletContext = createContext<WalletState | null>(null);

export function WalletProvider({ children }: { children: ReactNode }) {
  const provider = typeof window !== "undefined" ? window.ethereum : undefined;
  const [address, setAddress] = useState<Hex | null>(null);
  const [chainId, setChainId] = useState<number | null>(null);
  const [balance, setBalance] = useState<bigint | null>(null);
  const [connecting, setConnecting] = useState(false);
  const disconnected = useRef(false);

  const publicClient = useMemo(
    () =>
      createPublicClient({
        chain: robinhoodTestnet,
        // Bounded timeout + one safe retry per endpoint; optional approved
        // fallback RPC (env-configured) is tried only if the primary fails.
        transport: fallback(
          [
            http(ACTIVE_CHAIN.rpcUrl, { timeout: 10_000, retryCount: 1 }),
            ...(ACTIVE_CHAIN.rpcFallbackUrl
              ? [http(ACTIVE_CHAIN.rpcFallbackUrl, { timeout: 10_000, retryCount: 1 })]
              : []),
          ],
          { retryCount: 0 }
        ),
      }) as PublicClient,
    []
  );

  const walletClient = useMemo<WalletClient | null>(() => {
    if (!provider || !address) return null;
    return createWalletClient({
      account: address,
      chain: robinhoodTestnet,
      transport: custom(provider),
    });
  }, [provider, address]);

  const refreshChain = useCallback(async () => {
    if (!provider) return;
    try {
      const id = (await provider.request({ method: "eth_chainId" })) as string;
      setChainId(Number.parseInt(id, 16));
    } catch {
      setChainId(null);
    }
  }, [provider]);

  const applyAccounts = useCallback((accounts: unknown) => {
    const list = Array.isArray(accounts) ? (accounts as string[]) : [];
    setAddress(list.length > 0 && list[0] ? (getAddress(list[0]) as Hex) : null);
  }, []);

  // Restore an existing authorisation (no popup) unless the user disconnected.
  useEffect(() => {
    if (!provider) return;
    void (async () => {
      const accounts = await provider.request({ method: "eth_accounts" }).catch(() => []);
      if (!disconnected.current) applyAccounts(accounts);
      await refreshChain();
    })();
    const onAccounts = (a: unknown) => applyAccounts(a);
    const onChain = (c: unknown) => setChainId(typeof c === "string" ? Number.parseInt(c, 16) : null);
    provider.on?.("accountsChanged", onAccounts);
    provider.on?.("chainChanged", onChain);
    return () => {
      provider.removeListener?.("accountsChanged", onAccounts);
      provider.removeListener?.("chainChanged", onChain);
    };
  }, [provider, applyAccounts, refreshChain]);

  // Track balance for the active account.
  useEffect(() => {
    let alive = true;
    if (!address) {
      setBalance(null);
      return;
    }
    void publicClient
      .getBalance({ address })
      .then((b) => alive && setBalance(b))
      .catch(() => alive && setBalance(null));
    return () => {
      alive = false;
    };
  }, [address, chainId, publicClient]);

  const connect = useCallback(async () => {
    if (!provider) return;
    setConnecting(true);
    disconnected.current = false;
    try {
      const accounts = await provider.request({ method: "eth_requestAccounts" });
      applyAccounts(accounts);
      await refreshChain();
    } finally {
      setConnecting(false);
    }
  }, [provider, applyAccounts, refreshChain]);

  const disconnect = useCallback(() => {
    disconnected.current = true;
    setAddress(null);
    setBalance(null);
  }, []);

  const addTestnet = useCallback(async () => {
    if (!provider) return;
    await provider.request({
      method: "wallet_addEthereumChain",
      params: [
        {
          chainId: `0x${ACTIVE_CHAIN.chainId.toString(16)}`,
          chainName: ACTIVE_CHAIN.label,
          nativeCurrency: ACTIVE_CHAIN.nativeCurrency,
          rpcUrls: [ACTIVE_CHAIN.rpcUrl],
          blockExplorerUrls: [ACTIVE_CHAIN.explorerUrl],
        },
      ],
    });
  }, [provider]);

  const switchToTestnet = useCallback(async () => {
    if (!provider) return;
    try {
      await provider.request({
        method: "wallet_switchEthereumChain",
        params: [{ chainId: `0x${ACTIVE_CHAIN.chainId.toString(16)}` }],
      });
    } catch (e) {
      // 4902 = chain not added
      if (typeof e === "object" && e && "code" in e && (e as { code: number }).code === 4902) {
        await addTestnet();
      } else {
        throw e;
      }
    }
    await refreshChain();
  }, [provider, addTestnet, refreshChain]);

  const watchAsset = useCallback(
    async (token: { address: Hex; symbol: string; decimals: number }) => {
      if (!provider) return false;
      const ok = await provider.request({
        method: "wallet_watchAsset",
        params: { type: "ERC20", options: token } as unknown as object,
      });
      return Boolean(ok);
    },
    [provider]
  );

  const value: WalletState = {
    address,
    isConnected: !!address,
    chainId,
    balance,
    hasInjected: !!provider,
    connecting,
    publicClient,
    walletClient,
    connect,
    disconnect,
    switchToTestnet,
    addTestnet,
    watchAsset,
  };

  return <WalletContext.Provider value={value}>{children}</WalletContext.Provider>;
}

export function useWallet(): WalletState {
  const ctx = useContext(WalletContext);
  if (!ctx) throw new Error("useWallet must be used within WalletProvider");
  return ctx;
}
